// cspell:ignore Comlink
import {useEffect, useRef, useMemo, useCallback, useState} from 'react'
import _ from 'lodash'
import {useSelectedVariantContext} from '../components/step2/context/SelectedVariantContext'
import {handleCaptureArtworkWithFilter} from '../components/step2/context/SelectedVariantContext/helper'
import {actionUpdatePreviewImages} from '../components/step2/context/SelectedVariantContext/action'
import {useCustomShapeWorker} from './useCustomShapeWorker'
import {useIsCustomShape} from './useIsCustomShape'
import {createDebugger} from '../helpers/Debugger'
import PubSub from '../components/step2/Broker'

const debug = createDebugger({namespace: 'usePreviewCustomShape'})

/**
 * Hook to manage Pixi custom shape preview rendering.
 * Bridges React context (selectedSide, designs, safeZone, …) with the worker-backed
 * high-level API provided by useCustomShapeWorker.
 *
 * - cutSide is synced reactively: whenever useEditorSafeZone reports a new safeZone,
 *   setCutSide is called automatically once the worker is ready.
 *
 * @returns {{
 *   isCustomShape: boolean,
 *   isReady: boolean,
 *   isUpdating: boolean,
 *   pixiCanvasRef: React.RefObject<HTMLCanvasElement | null>,
 *   mockupImages: (string|null)[],
 *   getMockupImages: (sides?: string[]) => Promise<(string|null)[]>,
 *   getArtworkImage: () => Promise<string|null>,
 * }}
 */
export function usePreviewCustomShape() {
    const {
        state: {selectedSide, loadingImageLayer, selectedAttribute, designs, pickerAttributes},
        dispatch,
    } = useSelectedVariantContext()

    const [mockupImages, setMockupImages] = useState([])

    // Derived state during render (no effects needed)
    const isCustomShape = useIsCustomShape()

    // ALWAYS call all hooks regardless of isCustomShape (React Rules of Hooks)
    const pixiCanvasRef = useRef(null)

    // Document JSON for worker INIT — depends only on attribute, NOT on side.
    // Switching sides will NOT re-init the worker.
    const initDocumentJson = useMemo(() => {
        if (!selectedAttribute) return null
        const infos = _.get(selectedAttribute, ['mockup', 'meta', 'mockup_infos'], [])
        return infos[0] ?? null
    }, [selectedAttribute])

    const {
        isReady,
        isUpdating,
        workerRef,
        initWorker,
        disposeWorker,
        resize,
        getMockupImages: _getMockupImages,
        getArtworkImage: _getArtworkImage,
        setBackground,
        setCutSide,
        setPrintSide,
    } = useCustomShapeWorker()

    // --- Worker initialization (guarded by isCustomShape) ---
    useEffect(() => {
        if (!isCustomShape || !initDocumentJson) return

        let cancelled = false
        ;(async () => {
            try {
                await initWorker({canvas: pixiCanvasRef.current, documentJson: initDocumentJson})
            } catch (err) {
                if (!cancelled) debug.error('Worker init error:', err)
            }
        })()

        return () => {
            cancelled = true
            disposeWorker()
        }
    }, [isCustomShape, initDocumentJson, initWorker, disposeWorker])

    // --- ResizeObserver for canvas mode (guarded) ---
    useEffect(() => {
        if (!isCustomShape) return
        const container = pixiCanvasRef.current?.parentElement
        if (!container || !workerRef.current) return

        let timeoutId = null
        const observer = new ResizeObserver((entries) => {
            if (!entries[0] || !workerRef.current) return
            const {width, height} = entries[0].contentRect
            if (width && height) {
                clearTimeout(timeoutId)
                timeoutId = setTimeout(
                    () =>
                        resize({
                            width: Math.round(width),
                            height: Math.round(height),
                        }),
                    150,
                )
            }
        })

        observer.observe(container)
        return () => {
            observer.disconnect()
            clearTimeout(timeoutId)
        }
    }, [isCustomShape, isReady, workerRef])

    // --- Business-logic wrappers ---

    /**
     * Renders mockups for the given sides (default: ['front','back']).
     * Guards: isCustomShape, loadingImageLayer.
     * @param {string[]} [sides]
     * @returns {Promise<(string|null)[]>}
     */
    const getMockupImages = useCallback(
        async (sides = ['front', 'back']) => {
            if (!isCustomShape || selectedSide === 'All' || selectedSide === 'Safezone' || loadingImageLayer)
                return []
            return _getMockupImages(sides)
        },
        [isCustomShape, loadingImageLayer, selectedSide, _getMockupImages],
    )

    /**
     * Returns the current artwork layer as a data URL.
     * @returns {Promise<string|null>}
     */
    const getArtworkImage = useCallback(async () => {
        if (!isCustomShape) return null
        return _getArtworkImage()
    }, [isCustomShape, _getArtworkImage])

    /**
     * Resolves the Konva stage ref and safeZone for the given side.
     * @param {('front'|'back')} side
     * @returns {{stageRef: object, safeZone: object}|null}
     */
    const getStageData = useCallback(
        (side) => {
            const attr = pickerAttributes.find((a) => a._id === selectedAttribute?._id)
            if (!attr) return null
            const stageRef = attr.ref[side]
            const safeZone = attr.safeZone[side]
            return stageRef && safeZone ? {stageRef, safeZone} : null
        },
        [pickerAttributes, selectedAttribute],
    )

    const debounceUpdatePrintSide = useMemo(
        () =>
            _.debounce(() => {
                debug.debug('debounceUpdatePrintSide called')
                const sides = ['front', 'back']
                if (!isCustomShape || !sides.includes(selectedSide)) {
                    return
                }

                const stageData = getStageData(selectedSide)
                const printCanvas = handleCaptureArtworkWithFilter(stageData, {
                    excludeByName: ['background'],
                    pixelRatio: 1,
                })
                const printDataUrl = printCanvas.toDataURL('image/png')

                setPrintSide(printDataUrl, selectedSide)
            }, 500),
        [isCustomShape, selectedSide, getStageData, setPrintSide],
    )

    /**
     * Business logic: when receiving an UPDATE_3D_MODEL_TEXTURE event, trigger a mockup update.
     */
    useEffect(() => {
        if (!isCustomShape) return
        const subscriber = PubSub.subscribe('UPDATE_3D_MODEL_TEXTURE', () => {
            if (_.get(selectedAttribute, 'mockup.meta.use_client_mockup', false)) {
                debounceUpdatePrintSide()
            }
        })

        return () => {
            PubSub.unsubscribe(subscriber)
        }
    }, [selectedAttribute, isCustomShape, debounceUpdatePrintSide])

    useEffect(() => {
        if (!isReady || !isCustomShape || loadingImageLayer) return

        if (_.get(selectedAttribute, 'mockup.meta.use_client_mockup', false)) {
            debounceUpdatePrintSide()
        }

        return () => {
            debounceUpdatePrintSide.cancel()
        }
    }, [
        isReady,
        isCustomShape,
        pickerAttributes,
        designs,
        selectedAttribute,
        loadingImageLayer,
        selectedAttribute?.safeZone[selectedSide]?.fill,
        debounceUpdatePrintSide,
    ])

    const debounceUpdateCutSide = useMemo(
        () =>
            _.debounce((data) => {
                debug.debug('debounceUpdateCutSide called with data', data)
                if (data?.src === undefined) return
                setCutSide(data?.src ?? null, data?.side ?? 'front')
            }, 500),
        [setCutSide],
    )

    useEffect(() => {
        if (!isCustomShape) return
        const subscriber = PubSub.subscribe('UPDATE_CUSTOM_SHAPE_CUT_SIDE',  (data) => {
            debounceUpdateCutSide(data)
        })

        return () => {
            PubSub.unsubscribe(subscriber)
        }
    }, [selectedAttribute, isCustomShape, debounceUpdateCutSide])

    // --- Fetch mockup images when worker finishes updating ---
    useEffect(() => {
        if (!isCustomShape || !isReady || isUpdating) return
        let cancelled = false
        ;(async () => {
            const images = await getMockupImages()
            if (!cancelled) {
                setMockupImages(images)

                // Sync to store so other components (MockupImagePreviewContainer, VariantViewContainer) can access
                if (images.length && selectedAttribute) {
                    const selectedAttr = selectedAttribute.attributes.map((attr) => ({
                        type: attr.type,
                        value: attr.value_code,
                    }))
                    dispatch(actionUpdatePreviewImages([{
                        images,
                        attributes: [selectedAttr],
                        listSku: selectedAttribute.listSku || [],
                    }]))
                }
            }
        })()
        return () => {
            cancelled = true
        }
    }, [isCustomShape, isReady, isUpdating, getMockupImages, selectedAttribute, dispatch])

    return {
        isCustomShape,
        isReady: isCustomShape ? isReady : false,
        isUpdating: isCustomShape ? isUpdating : false,
        pixiCanvasRef,
        mockupImages,
        getMockupImages,
        getArtworkImage,
    }
}

